import { Dialog } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterModule } from '@angular/router';
import { CreateProviderAppointmentResponse, NavigationItem } from '@models';
import {
  AuthenticationService,
  ChatUnreadService,
  MeService,
  NavigationService,
  ProviderAppointmentsNotificationService,
  ToastService,
} from '@services';
import { BookAppointmentProviderDialogComponent } from '@ui-lib';
import { HelpCenterDialogComponent } from '../help-center-dialog/help-center-dialog.component';
import { catchError, EMPTY } from 'rxjs';
import { InvitePatientModalService } from '../../patient-list/services/invite-patient-modal.service';
import { VOICEMAIL_UNREAD_COUNT } from '../../../voicemail.tokens';

@Component({
  selector: 'provider-portal-sidebar-menu',
  imports: [NgClass, MatIconModule, RouterModule, RouterLink],
  templateUrl: './provider-portal-sidebar-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class ProviderPortalSidebarMenuComponent {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly navigationService = inject(NavigationService);
  private readonly meService = inject(MeService);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly invitePatientModal = inject(InvitePatientModalService);
  private readonly providerAppointmentsNotificationService = inject(
    ProviderAppointmentsNotificationService
  );
  private readonly toast = inject(ToastService);

  readonly me = this.meService.me;
  readonly avatarUrl = this.meService.avatarUrl;

  readonly totalUnreadCount = inject(ChatUnreadService).totalUnreadCount;
  readonly voicemailUnreadCount =
    inject(VOICEMAIL_UNREAD_COUNT, { optional: true }) ?? (() => 0);

  navItems = computed(
    () =>
      this.navigationService
        .getItems()
        .map(item =>
          item.children && item.children.length > 0
            ? { ...item, collapsed: false }
            : item
        ) as (NavigationItem & { collapsed: boolean })[]
  );

  providerItems = computed(() => this.navigationService.getProvidersItems());

  growthItems = computed(() => this.navigationService.getGrowthItems());

  careToolsItems = computed(() => this.navigationService.getCareToolsItems());

  get fullName(): string | undefined {
    const current = this.me();
    return current ? `${current.givenName} ${current.familyName}` : undefined;
  }

  get email(): string | null | undefined {
    return this.me()?.email;
  }

  get isSidebarOpen(): boolean {
    return this.navigationService.isSidebarOpen();
  }

  closeSidebar(): void {
    this.navigationService.closeSidebar();
  }

  logout(): void {
    this.authenticationService.logout(true);
    this.router.navigate(['/auth']);
  }

  openHelpCenterDialog(): void {
    this.dialog.open(HelpCenterDialogComponent);
  }

  async openNewAppointmentDialog(): Promise<void> {
    try {
      this.dialog
        .open<CreateProviderAppointmentResponse>(
          BookAppointmentProviderDialogComponent
        )
        .closed.pipe(
          catchError(() => {
            this.toast.showWarning('Failed to book an appointment.');
            return EMPTY;
          })
        )
        .subscribe(result => {
          if (result) {
            this.providerAppointmentsNotificationService.emit(result);
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
    } catch (error) {
      console.error(
        'Failed to load BookAppointmentProviderDialogComponent:',
        error
      );
    }
  }

  handleInvitePatient() {
    this.invitePatientModal.open().subscribe();
  }
}
